import * as React from "react";
import {
  RadarChart as RechartsRadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent } from "./chart";
import { cn } from "@/lib/utils";

export interface RadarChartProps {
  data: any[];
  className?: string;
  config?: any;
  variant?: "default" | "animated" | "interactive" | "minimal" | "dots" | "lines" | "filled";
  theme?: "light" | "dark" | "minimal";
  responsive?: boolean;
  accessible?: boolean;
  dataKeys?: string[];
  colors?: string[];
  strokeWidth?: number;
  fillOpacity?: number;
  showGrid?: boolean;
  showLegend?: boolean;
  showTooltip?: boolean;
  showDots?: boolean;
  showActiveDot?: boolean;
  dotRadius?: number;
  activeDotRadius?: number;
  height?: number | string;
  width?: number | string;
  gridType?: "polygon" | "circle";
  domain?: [number, number];
  angle?: number;
  tickLine?: boolean;
  axisLine?: boolean;
}

export function RadarChart({
  data,
  className,
  config = {},
  variant = "default",
  theme = "light",
  responsive = true,
  accessible = true,
  dataKeys = ["value"],
  colors = [
    "hsl(221.2 83.2% 53.3%)",
    "hsl(217.2 91.2% 59.8%)",
    "hsl(214.3 31.8% 91.4%)",
    "hsl(210 40% 96%)",
  ],
  strokeWidth = 2,
  fillOpacity = 0.3,
  showGrid = true,
  showLegend = false,
  showTooltip = true,
  showDots = false,
  showActiveDot = false,
  dotRadius = 4,
  activeDotRadius = 6,
  height,
  width,
  gridType = "polygon",
  domain = [0, 150],
  angle = 90,
  tickLine = false,
  axisLine = false,
  ...props
}: RadarChartProps) {
  // Determine colors based on theme
  const getThemeColors = () => {
    switch (theme) {
      case "dark":
        return [
          "#60a5fa",
          "#34d399",
          "#fbbf24",
          "#f87171",
        ];
      case "minimal":
        return [
          "#6b7280",
          "#9ca3af",
          "#d1d5db",
          "#e5e7eb",
        ];
      default:
        return colors;
    }
  };

  const themeColors = getThemeColors();

  // Determine variant-specific props
  const getVariantProps = () => {
    switch (variant) {
      case "animated":
        return {
          className: cn(className, "wl-radar-chart-animated"),
          animationDuration: 1500,
          animationEasing: "ease-out",
        };
      case "interactive":
        return {
          className: cn(className, "wl-radar-chart-interactive"),
        };
      case "minimal":
        return {
          className: cn(className, "wl-radar-chart-minimal"),
          strokeWidth: 1,
          fillOpacity: 0.1,
          showDots: false,
        };
      case "dots":
        return {
          className: cn(className, "wl-radar-chart-dots"),
          showDots: true,
          showActiveDot: true,
        };
      case "lines":
        return {
          className: cn(className, "wl-radar-chart-lines"),
          fillOpacity: 0,
        };
      case "filled":
        return {
          className: cn(className, "wl-radar-chart-filled"),
          fillOpacity: 0.5,
        };
      default:
        return {
          className,
        };
    }
  };

  const variantProps = getVariantProps();

  // Get fill opacity based on variant
  const getFillOpacity = () => {
    if (variant === "lines") return 0;
    if (variant === "minimal") return 0.1;
    if (variant === "filled") return 0.5;
    return fillOpacity;
  };

  // Get stroke width based on variant
  const getStrokeWidth = () => {
    if (variant === "minimal") return 1;
    return strokeWidth;
  };

  return (
    <div
      className={cn(
        "wl-radar-chart",
        "wl-radar-chart-theme",
        `wl-radar-chart-theme--${theme}`,
        variant !== "default" && `wl-radar-chart-${variant}`,
        responsive && "wl-radar-chart-responsive",
        accessible && "wl-radar-chart-accessible",
        className
      )}
      data-chart-type="radar"
      style={{ height, width }}
      {...props}
    >
      <ChartContainer config={config}>
        <RechartsRadarChart
          data={data}
          margin={{ left: 20, right: 20, top: 20, bottom: 20 }}
          {...variantProps}
        >
          {showGrid && (
            <PolarGrid gridType={gridType} />
          )}
          
          <PolarAngleAxis dataKey="subject" />
          
          <PolarRadiusAxis
            angle={angle}
            domain={domain}
            tickLine={tickLine}
            axisLine={axisLine}
          />
          
          {showTooltip && (
            <ChartTooltip content={<ChartTooltipContent />} />
          )}
          
          {showLegend && (
            <ChartLegend content={<ChartLegendContent />} />
          )}
          
          {dataKeys.map((dataKey, index) => (
            <Radar
              key={dataKey}
              name={dataKey}
              dataKey={dataKey}
              stroke={themeColors[index % themeColors.length]}
              fill={themeColors[index % themeColors.length]}
              fillOpacity={getFillOpacity()}
              strokeWidth={getStrokeWidth()}
              dot={
                showDots
                  ? {
                      fill: themeColors[index % themeColors.length],
                      strokeWidth: 2,
                      r: dotRadius,
                    }
                  : false
              }
              activeDot={
                showActiveDot
                  ? {
                      r: activeDotRadius,
                      strokeWidth: 2,
                      fill: themeColors[index % themeColors.length],
                    }
                  : false
              }
              animationDuration={variant === "animated" ? 1500 : undefined}
              animationEasing={variant === "animated" ? "ease-out" : undefined}
            />
          ))}
        </RechartsRadarChart>
      </ChartContainer>
    </div>
  );
}

// Export for convenience
export { RadarChart as default }; 